import { css } from '@emotion/react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import styled from '@emotion/styled';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, memo, useCallback } from 'react';
import { OptionsListSpaceVariants, } from '@polestar/component-warehouse-react/components/options-list/OptionsList';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Checkbox from '@polestar/component-warehouse-react/components/checkbox/Checkbox';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const OptionsListOption = memo(forwardRef(({ id, children, labelId, value, index, disabled = false, selected, renderOption, isGroupOption = false, isGroupLabel = false, isMultiple = false, internal = false, onClick, hasNoOptions = false, spacingVariant = OptionsListSpaceVariants.default, ...props }, ref) => {
    const memoizedId = useSSRSafeId('options-list', id);
    const handleClick = useCallback((e) => {
        e.stopPropagation();
        onClick === null || onClick === void 0 ? void 0 : onClick(value);
    }, [onClick, value]);
    if (value) {
        disabled = (value === null || value === void 0 ? void 0 : value.disabled) === true;
    }
    const iconBeforeLabel = isMultiple && internal;
    const icon = iconBeforeLabel ? (React.createElement(React.Fragment, null,
        React.createElement(Checkbox, { checked: selected, disabled: disabled, indeterminate: selected === undefined, "aria-hidden": true, readOnly: true, tabIndex: -1 }),
        React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: Spacings.medium }))) : (selected !== false && !isGroupLabel) ||
        (selected !== false && isGroupLabel && isMultiple) ? (React.createElement(React.Fragment, null,
        React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: Spacings.small }),
        React.createElement(Checkmark, { pairedWithText: false, icon: selected === undefined ? Icons.minus : Icons.checkmark, size: IconSizes.small, isInternal: internal }))) : (React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: createCustomSpacing(28 / 16) }));
    return (React.createElement(OptionWrapper, { ...(isGroupLabel
            ? {}
            : aria({
                disabled: (value === null || value === void 0 ? void 0 : value.disabled) === true,
                selected: selected,
                describedby: labelId,
                role: 'option',
            })), ...props, id: memoizedId, key: memoizedId, ref: ref, disabled: disabled, selected: selected || false, isGroupOption: isGroupOption, isGroupLabel: isGroupLabel, isMultiple: isMultiple, isInternal: internal, hasNoOptions: hasNoOptions, spacingVariant: spacingVariant, ...(disabled || (isGroupLabel && !isMultiple) ? {} : { onClick: handleClick }) },
        iconBeforeLabel && icon,
        children,
        renderOption ? renderOption(value, index) : value,
        !iconBeforeLabel && icon));
}));
OptionsListOption.displayName = 'OptionsListOption';
export default OptionsListOption;
const OptionWrapper = styled.button(({ theme, disabled, selected, isGroupOption, isGroupLabel, isMultiple, isInternal, hasNoOptions, spacingVariant, }) => {
    const verticalInternalSpacing = Spacings.small;
    const internalPadding = {
        mobile: `
        ${verticalInternalSpacing} ${Spacings.large}
      `,
        tablet: spacingVariant === OptionsListSpaceVariants.fullWidth &&
            `
          ${verticalInternalSpacing} ${Spacings.medium}
        `,
    };
    return css(css `
        background-color: ${theme.optionsList.backgroundColor};
        border: none;
        width: 100%;
        text-align: start;
        display: flex;
        flex-grow: 1;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        position: relative;
        min-height: ${Spacings.xxLarge};
        -webkit-tap-highlight-color: ${theme.optionsList.states.hover.backgroundColor};
      `, isInternal &&
        isMultiple &&
        css `
          justify-content: flex-start;
        `, !disabled &&
        css `
          cursor: pointer;
          color: ${theme.optionsList.states.enabled.labelColor};

          @media (hover: hover) {
          &:hover {
            color: ${theme.optionsList.highlightedLabelColor}
            box-shadow: none;
            background-color: ${theme.optionsList.states.hover.backgroundColor};
          }
        }
        `, isGroupLabel &&
        css `
          color: ${theme.optionsList.states.enabled.groupLabelColor};
          ${responsiveStyles({
            padding: internalPadding,
        })};
        `, isGroupLabel &&
        isGroupOption &&
        !isMultiple &&
        css `
          &:hover {
            cursor: default;
            background-color: transparent;
          }
        `, !isGroupLabel &&
        isGroupOption &&
        css `
          ${responsiveStyles({
            /* this is needed while the `padding` shorthand is not picked up in RTL mode */
            paddingBlockStart: { mobile: verticalInternalSpacing },
            paddingBlockEnd: { mobile: verticalInternalSpacing },
            paddingInlineStart: {
                mobile: createCustomSpacing(36 / 16),
                tablet: spacingVariant === OptionsListSpaceVariants.fullWidth &&
                    createCustomSpacing(28 / 16),
            },
            paddingInlineEnd: {
                mobile: Spacings.large,
                tablet: spacingVariant === OptionsListSpaceVariants.fullWidth && Spacings.medium,
            },
        })};
        `, !isGroupLabel &&
        !isGroupOption &&
        css `
          ${responsiveStyles({
            padding: internalPadding,
        })}
        `, (isGroupLabel && !isMultiple && selected) ||
        (isGroupLabel &&
            !isMultiple &&
            css `
            cursor: default;
          `), selected &&
        css `
          color: ${theme.optionsList.states.active.labelColor};
        `, disabled &&
        css `
          color: ${theme.optionsList.states.disabled.labelColor};
          user-select: none;
        `, hasNoOptions &&
        css `
          color: ${theme.optionsList.states.noOptions.labelColor};
          user-select: none;
        `);
});
const Checkmark = styled(Icon)(({ isInternal, theme }) => isInternal &&
    css `
      border-radius: 50%;
      padding: ${Spacings.xxSmall};
      width: ${Spacings.large};
      height: ${Spacings.large};
      background-color: ${theme.optionsList.internalCheckmarkBackground};
      color: ${theme.optionsList.internalCheckmarkColor};
    `);
